import { formatInTimeZone } from 'date-fns-tz';

import { GlucoseReading } from './glucose-types';

export type DailyGlucoseSummary = {
  dayKey: string;
  dateLabel: string;
  readings: GlucoseReading[];
  average: number;
  minimum: number;
  maximum: number;
};

export type GlucoseSummary = {
  readings: GlucoseReading[];
  days: DailyGlucoseSummary[];
  average: number | null;
  minimum: number | null;
  maximum: number | null;
};

const mean = (values: number[]): number =>
  Math.round(values.reduce((total, value) => total + value, 0) / values.length);

export function buildGlucoseSummary(
  readings: GlucoseReading[],
  timezone: string,
): GlucoseSummary {
  if (readings.length === 0) {
    return {
      readings: [],
      days: [],
      average: null,
      minimum: null,
      maximum: null,
    };
  }

  const chronologicalReadings = readings
    .map((glucoseReading, inputIndex) => ({ glucoseReading, inputIndex }))
    .sort(
      (left, right) =>
        Date.parse(left.glucoseReading.observedAt) -
          Date.parse(right.glucoseReading.observedAt) ||
        left.glucoseReading.observedAt.localeCompare(
          right.glucoseReading.observedAt,
        ) ||
        left.inputIndex - right.inputIndex,
    )
    .map(({ glucoseReading }) => glucoseReading);

  const readingsByDay = new Map<string, GlucoseReading[]>();
  chronologicalReadings.forEach((glucoseReading) => {
    const dayKey = formatInTimeZone(
      glucoseReading.observedAt,
      timezone,
      'yyyy-MM-dd',
    );
    const dayReadings = readingsByDay.get(dayKey) ?? [];
    dayReadings.push(glucoseReading);
    readingsByDay.set(dayKey, dayReadings);
  });

  const days = [...readingsByDay.entries()].map(
    ([dayKey, dayReadings]): DailyGlucoseSummary => {
      const firstReading = dayReadings[0];
      if (!firstReading) {
        throw new Error(`Glucose day ${dayKey} contains no readings.`);
      }
      const values = dayReadings.map(({ value }) => value);
      return {
        dayKey,
        dateLabel: formatInTimeZone(
          firstReading.observedAt,
          timezone,
          'MMM d, yyyy',
        ),
        readings: dayReadings,
        average: mean(values),
        minimum: Math.min(...values),
        maximum: Math.max(...values),
      };
    },
  );
  const values = chronologicalReadings.map(({ value }) => value);

  return {
    readings: chronologicalReadings,
    days,
    average: mean(values),
    minimum: Math.min(...values),
    maximum: Math.max(...values),
  };
}

export function getReadingsForDay(
  summary: GlucoseSummary,
  dayKey: string,
): GlucoseReading[] {
  return summary.days.find((day) => day.dayKey === dayKey)?.readings ?? [];
}

export function formatGlucoseDateRange(
  summary: GlucoseSummary,
  timezone: string,
): string {
  const firstDay = summary.days[0];
  const lastDay = summary.days.at(-1);
  if (!firstDay || !lastDay) return '';
  if (firstDay.dayKey === lastDay.dayKey) return firstDay.dateLabel;

  const firstReading = firstDay.readings[0];
  const lastReading = lastDay.readings.at(-1);
  if (!firstReading || !lastReading) return '';
  const firstYear = formatInTimeZone(firstReading.observedAt, timezone, 'yyyy');
  const lastYear = formatInTimeZone(lastReading.observedAt, timezone, 'yyyy');
  const firstLabel = formatInTimeZone(
    firstReading.observedAt,
    timezone,
    firstYear === lastYear ? 'MMM d' : 'MMM d, yyyy',
  );
  const lastLabel = formatInTimeZone(
    lastReading.observedAt,
    timezone,
    'MMM d, yyyy',
  );

  return `${firstLabel} – ${lastLabel}`;
}
